import produce from 'immer';
import { Action, createModel } from '@rematch/core';
import {
  checkoutAppointment,
  isCheckoutPaymentError,
  isCheckoutSuccess,
} from '../../api/Checkout/CheckoutAppointment';
import { checkoutAppointmentPreview } from '../../api/Checkout/CheckoutAppointmentPreview';
import {
  ProAppointment,
  ProAppointmentDetails,
  createProAppointment,
  getProAppointmentDetails,
  PaymentOption,
  updateProAppointment,
  ClientCardOnFile,
  removeSmartPrice,
} from '../../api/Providers/Appointments';
import { cancelAppointmentById } from '../../api/Providers/Appointments/AppointmentsCancellation';
import { markNoShowAppointment } from '../../api/Providers/Appointments/AppointmentsNoShow';
import { deleteAppointment } from '../../api/Providers/Appointments/AppointmentsDelete';
import { undoCheckout } from '../../api/Providers/Appointments/AppointmentsUndoCheckout';
import nonCriticalException from '../../modules/exceptionLogger';
import {
  canUseCardOnFile,
  CheckoutTotals,
  deletePaymentMethod,
  getCheckoutDetails,
  getPaymentType,
  paymentTypesAreDiscountable,
} from '../../modules/provider/appointmentCheckout';
import { changeAppointmentDate } from '../../modules/provider/Appointments';
import { loadCard } from '../../modules/provider/ClientCard';
import { selectors as userSelectors } from '../CurrentUser.model';
import type { RootModel } from '../models';
import { ICard, InstrumentType } from '../PaymentMethods';
import { PrivilegedProvider } from '../../api/Providers';

export type CheckoutState = {
  discountAmount: number;
  discountCode?: string;
  tip: string;
  paymentType: PaymentOption;
  card?: ICard;
  cardOnFile?: ClientCardOnFile;
};

export type CheckoutDetails = ProAppointment & CheckoutTotals & CheckoutState;

export type ProAppointmentUpdatePayload = Partial<ProAppointment & {
  waitlist_id: ProAppointmentDetails['waitlist_id'];
  smart_price_source: string;
}>;

export type ProviderManageAppointmentState = {
  selectedAppointmentId: number | null;
  providerId: number | null;
  details: {
    [id: number]: ProAppointmentDetails;
  },
  loading: boolean;

  checkout: CheckoutState,
};

type AppointmentPayload = {
  appointment: ProAppointmentDetails;
};

type AppointmentIdPayload = {
  appointmentId: number;
};

type IdPayload = AppointmentIdPayload & {
  providerId: number;
};

type CancelPayload = IdPayload & {
  cancelAll?: boolean;
};

type NoShowPayload = IdPayload & {
  delete?: boolean;
  charge?: number | string;
};

type UpdatePayload = {
  appointment: ProAppointmentUpdatePayload;
  providerId: number;
};

type CheckoutPayload = {
  signature?: string;
};

type UpdatePaymentTypePayload = {
  paymentType: PaymentOption;
  card: ICard | null;
};

type CreateCheckoutPayload = UpdatePaymentTypePayload & {
  discountAmount: number;
  discountCode?: string;
  tip: string;
  cardOnFile?: ClientCardOnFile | null;
};

type UpdateTipPayload = {
  tip: string | number;
};

export type Discount = {
  discountCode?: string | null;
  discountAmount: number;
};

function getInitialState(): ProviderManageAppointmentState {
  return {
    providerId: null,
    selectedAppointmentId: null,
    loading: false,
    details: {},
    checkout: {
      discountAmount: 0,
      tip: '0',
      paymentType: null,
      card: undefined,
      cardOnFile: undefined,
    },
  };
}

function getDiscountFromCheckout({
  paymentType,
  discountAmount,
  discountCode,
}: CheckoutState): Discount {
  if (!paymentTypesAreDiscountable(paymentType)) {
    return {
      discountAmount: 0,
      discountCode: null,
    };
  }
  return {
    discountAmount,
    discountCode,
  };
}

/**
 *  providerManageAppointment model
 *  exposes a model that allows a provider to manage an appointment -
 *  used in providerDetails, providerAppointmentEdit, providerAppointmentCreate and
 *  the providerCalendar.
 * */
export const providerManageAppointment = createModel<RootModel>()({
  name: 'providerManageAppointment',

  state: getInitialState() as ProviderManageAppointmentState,

  reducers: {
    'user/logout': () => getInitialState(),

    onLoad(
      state: ProviderManageAppointmentState,
      payload: IdPayload,
    ): ProviderManageAppointmentState {
      return {
        ...state,
        ...payload,
        loading: true,
      };
    },

    onLoaded: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      state.selectedAppointmentId = appointment.id;
      state.loading = false;
      return state;
    }),

    onLanded: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      state.selectedAppointmentId = appointment.id;
      state.loading = false;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onCreated: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onUpdated: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onRescheduled: produce<ProviderManageAppointmentState, [AppointmentPayload]>((
      state: ProviderManageAppointmentState,
      { appointment }: AppointmentPayload,
    ): ProviderManageAppointmentState => {
      state.details[appointment.id] = appointment;
      return state;
    }),

    // note: the calendar model also listens to this reducer
    onCancellation: produce<ProviderManageAppointmentState, [AppointmentIdPayload]>((
      state: ProviderManageAppointmentState,
      { appointmentId }: AppointmentIdPayload,
    ) => {
      delete state.details[appointmentId];
      return state;
    }),

    onCheckoutCreated: produce<ProviderManageAppointmentState, [CreateCheckoutPayload]>((
      state: ProviderManageAppointmentState,
      {
        discountAmount,
        tip,
        discountCode,
        paymentType,
        card,
        cardOnFile,
      }: CreateCheckoutPayload,
    ) => {
      state.checkout.discountAmount = discountAmount;
      state.checkout.discountCode = discountCode;
      state.checkout.tip = tip;
      state.checkout.paymentType = paymentType;
      state.checkout.card = card;
      state.checkout.cardOnFile = cardOnFile;
      return state;
    }),

    onTipUpdated: produce<ProviderManageAppointmentState, [UpdateTipPayload]>((
      state: ProviderManageAppointmentState,
      { tip }: UpdateTipPayload,
    ) => {
      const roundedTip = Number(tip).toFixed(2);
      state.checkout.tip = roundedTip;
    }),

    onPaymentTypeUpdated: produce<ProviderManageAppointmentState, [UpdatePaymentTypePayload]>((
      state: ProviderManageAppointmentState,
      { paymentType, card }: UpdatePaymentTypePayload,
    ) => {
      state.checkout.paymentType = paymentType;
      state.checkout.card = card;
    }),
  },

  effects: dispatch => ({
    async land(
      { appointmentId }: AppointmentIdPayload,
      rootState,
    ) {
      const providerId = userSelectors.getSuperAwareProviderId(rootState);
      const appointment = await getProAppointmentDetails(providerId, appointmentId);
      const provider = await dispatch.providers.loadProvider({
        providerId,
        setActive: true,
      }) as PrivilegedProvider;
      await dispatch.providerManageAppointment.createCheckout({ appointment, provider });
      dispatch.providerManageAppointment.onLanded({
        appointment,
      });
      return {
        appointment,
        provider,
      };
    },

    async load({ appointmentId, providerId }: IdPayload): Promise<ProAppointment> {
      dispatch.providerManageAppointment.onLoad({ appointmentId, providerId });
      const appointment = await getProAppointmentDetails(providerId, appointmentId);
      dispatch.providerManageAppointment.onLoaded({
        appointment,
      });
      return appointment;
    },

    async create(
      { appointment, providerId }: UpdatePayload,
      rootState,
    ): Promise<ProAppointment> {
      const response = await createProAppointment(providerId, {
        ...appointment,
        booked_by: rootState?.user?.user_id ?? rootState?.user?.userId,
      });

      dispatch.providerManageAppointment.onCreated({ appointment: response });
      return response;
    },

    async update(
      { appointment, providerId }: UpdatePayload,
    ): Promise<void> {
      await updateProAppointment(providerId, appointment);
      await dispatch.providerManageAppointment.load({
        providerId,
        appointmentId: appointment.id,
      });
    },

    async cancel({
      appointmentId, providerId, cancelAll,
    }: CancelPayload) {
      const response = await cancelAppointmentById(providerId, appointmentId, cancelAll);
      dispatch.providerManageAppointment.onCancellation({ appointmentId });
      return response;
    },

    async checkout({ signature }: CheckoutPayload = {}, rootState) {
      // Rebuild all checkoutDetails from store
      const { proCheckoutSettings } = rootState;
      const {
        checkout: checkoutState,
        details,
        selectedAppointmentId,
      } = rootState.providerManageAppointment as ProviderManageAppointmentState;
      const appointment = details[selectedAppointmentId];

      const isPaymentTypeDiscountable = paymentTypesAreDiscountable(checkoutState.paymentType);
      const applicableDiscount = isPaymentTypeDiscountable
        ? checkoutState.discountAmount
        : 0;
      const applicableDiscountCode = isPaymentTypeDiscountable
        ? checkoutState.discountCode
        : null;

      const checkoutDetails = getCheckoutDetails(
        appointment,
        proCheckoutSettings,
        {
          discountCode: applicableDiscountCode,
          discountAmount: applicableDiscount,
        },
        checkoutState,
      );

      if (!checkoutDetails) {
        throw new Error('Not enough information to generate checkout');
      }

      // Actually check out the appointment
      const response = await checkoutAppointment(appointment.id, {
        ...checkoutDetails,
        signature,
      });

      if (!isCheckoutSuccess(response)) {
        if (isCheckoutPaymentError(response)) {
          throw new Error(response.error_message);
        }

        // eslint-disable-next-line no-underscore-dangle
        throw new Error(response?.__all__?.[0] || 'Error occurred checking out this appointment');
      }

      const updatedAppt = await getProAppointmentDetails(appointment.provider, appointment.id);
      dispatch.providerManageAppointment.onUpdated({ appointment: updatedAppt });

      // localstorage cleanup
      deletePaymentMethod(appointment.id);
      return appointment;
    },

    async undoCheckout({ appointmentId, providerId }: IdPayload) {
      await undoCheckout(providerId, appointmentId);
      const updatedAppt = await getProAppointmentDetails(providerId, appointmentId);
      dispatch.providerManageAppointment.onUpdated({ appointment: updatedAppt });
      return updatedAppt;
    },

    async markNoShow({
      appointmentId,
      providerId,
      delete: markDeleted,
      charge,
    }: NoShowPayload) {
      const opts: { charge: string | false } = {
        charge: false,
      };
      if (charge) {
        opts.charge = Number(charge).toFixed(2);
      }
      if (markDeleted) {
        await deleteAppointment(appointmentId, opts);
      } else {
        await markNoShowAppointment(appointmentId, opts);
      }
      const updatedAppt = await getProAppointmentDetails(providerId, appointmentId);
      await dispatch.providerManageAppointment.onUpdated({ appointment: updatedAppt });
      dispatch.providerClients.loadClient({
        providerId,
        clientId: updatedAppt.client?.id,
      });
      return updatedAppt;
    },

    async removeSmartPricing({
      appointmentId,
      providerId,
    }: IdPayload) {
      await removeSmartPrice(providerId, appointmentId);
      await dispatch.providerManageAppointment.load({
        appointmentId,
        providerId,
      });
    },

    async reschedule({
      appointmentId, providerId, newDate,
    }: IdPayload & { newDate: string }): Promise<any> {
      const apptPayload = changeAppointmentDate(
        await getProAppointmentDetails(providerId, appointmentId),
        newDate,
      );
      const response = await updateProAppointment(providerId, apptPayload);
      dispatch.providerManageAppointment.onRescheduled({ appointment: response });
      return response;
    },

    async createCheckout({
      appointment,
      provider,
    }: {
      appointment: ProAppointmentDetails,
      provider: PrivilegedProvider,
    }): Promise<
      Action<UpdatePaymentTypePayload & {
        discountAmount: number;
        discountCode?: string;
        tip: string;
        cardOnFile?: ClientCardOnFile;
      }, void>
      > {
      const proCheckoutSettingsPromise = dispatch.proCheckoutSettings.load({
        providerId: appointment.provider,
      });

      const checkoutPreview = await checkoutAppointmentPreview(
        appointment.id,
        Number(appointment.cost),
      );

      let card: ClientCardOnFile | null = null;
      if (appointment.client?.id) {
        try {
          [card] = await loadCard(appointment.client?.id, appointment.id);
        } catch (e) {
          nonCriticalException(e);
          console.error('Could not load the client card on file', e);
        }
      }
      const paymentType = getPaymentType(appointment, provider, card);
      const cardForCheckout: ICard | null = card ? {
        ...card,
        is_default: false,
        instrument_type: InstrumentType.Card,
      } : null;

      await proCheckoutSettingsPromise;

      return dispatch.providerManageAppointment.onCheckoutCreated({
        discountAmount: Number(checkoutPreview.discount?.amount || 0),
        discountCode: checkoutPreview.discount?.code,
        tip: appointment.checkout?.tip || '0',
        paymentType,
        card: canUseCardOnFile(appointment, provider) ? cardForCheckout : null,
        cardOnFile: card,
      });
    },

  }),

  selectors: (slice, createSelector, hasProps) => ({
    // returns the selected appointment
    appointment: models => createSelector(
      slice(state => state.details),
      models.providerCalendar.appointmentsById,
      slice(state => state.selectedAppointmentId),
      (appointments, calendarAppts, apptId): ProAppointmentDetails => (
        appointments[apptId] ?? calendarAppts[apptId] as ProAppointmentDetails
      ),
    ),

    forId: hasProps((models: any, id: number) => (
      createSelector(
        slice(state => state.details),
        details => details[id],
      )
    )),

    applicableDiscount() {
      return createSelector(
        slice(state => state.checkout),
        getDiscountFromCheckout,
      );
    },

    checkoutDetails(models) {
      return createSelector(
        slice,
        models.proCheckoutSettings.all,
        models.providerCalendar.appointmentsById,
        (manageAppointmentState, checkoutSettingsState, calendarAppts): CheckoutDetails => {
          const apptId = manageAppointmentState.selectedAppointmentId;
          const appt = (
            manageAppointmentState.details[apptId] ?? calendarAppts[apptId]
          ) as ProAppointmentDetails;

          return getCheckoutDetails(
            appt,
            checkoutSettingsState,
            getDiscountFromCheckout(manageAppointmentState.checkout),
            manageAppointmentState.checkout,
          );
        },
      );
    },

    isUsingCardOnFile() {
      return createSelector(
        slice(state => state.checkout.card),
        slice(state => state.checkout.cardOnFile),
        (checkoutCard, cardOnFile): boolean => (
          !!checkoutCard
          && checkoutCard.id === cardOnFile?.id
        ),
      );
    },
  }),
});
